import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AutocompleteField - single-value text input with workspace-scoped suggestions
 *
 * Displays a plain text input that opens a suggestion dropdown populated with
 * previously-entered workspace values. Users can freely type any value (creatable)
 * or pick from the dropdown. Built on Radix Popover following the same patterns
 * as MultiSelect.
 *
 * Features:
 * - Type to filter suggestions (case-insensitive substring)
 * - Click or keyboard (↑↓ Enter) to select a suggestion
 * - Escape to dismiss; free text always accepted
 * - Dropdown hidden when options list is empty or loading
 * - Follows Protoss design tokens - no inline styles, no hard-coded colours
 */
import * as Popover from '@radix-ui/react-popover';
import * as React from 'react';
const MAX_VISIBLE = 8;
export function AutocompleteField({ label, value, onChange, options, placeholder, disabled = false, loading = false, error, id, name, ariaLabel, className, size = 'compact', normalize, prefix, onSelect, }) {
    const inputId = id || React.useId();
    const [open, setOpen] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const inputRef = React.useRef(null);
    const controlRef = React.useRef(null);
    const prefixRef = React.useRef(null);
    const suppressNextFocusOpenRef = React.useRef(false);
    // Measure prefix width so the input padding-left is exact
    const [prefixWidth, setPrefixWidth] = React.useState(0);
    React.useLayoutEffect(() => {
        if (prefixRef.current) {
            setPrefixWidth(prefixRef.current.getBoundingClientRect().width);
        }
    }, [prefix]);
    const filteredOptions = React.useMemo(() => {
        const q = value.trim().toLowerCase();
        const matches = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
        return matches.slice(0, MAX_VISIBLE);
    }, [options, value]);
    // True when the trimmed input value is non-empty and not already in the suggestions.
    const showCreateOption = React.useMemo(() => {
        const trimmed = value.trim();
        if (!trimmed)
            return false;
        return !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());
    }, [options, value]);
    // Reset keyboard highlight whenever the filtered list changes
    React.useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredOptions, showCreateOption]);
    const handleSelect = (option) => {
        const normalized = normalize ? normalize(option) : option;
        onChange(normalized);
        onSelect?.(normalized);
        setOpen(false);
        setHighlightedIndex(-1);
        suppressNextFocusOpenRef.current = true;
        inputRef.current?.focus();
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
            setHighlightedIndex(-1);
            e.preventDefault();
            return;
        }
        // Total items = suggestions + optional create row
        const totalItems = filteredOptions.length + (showCreateOption ? 1 : 0);
        if (e.key === 'ArrowDown') {
            if (!open)
                setOpen(true);
            setHighlightedIndex((i) => Math.min(i + 1, totalItems - 1));
            e.preventDefault();
            return;
        }
        if (e.key === 'ArrowUp') {
            setHighlightedIndex((i) => Math.max(i - 1, -1));
            e.preventDefault();
            return;
        }
        if (e.key === 'Enter') {
            // Create option is always the last row
            if (showCreateOption && highlightedIndex === filteredOptions.length) {
                handleSelect(value.trim());
                e.preventDefault();
                return;
            }
            if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                handleSelect(filteredOptions[highlightedIndex]);
                e.preventDefault();
            }
        }
    };
    const showDropdown = open && !disabled && !loading && (filteredOptions.length > 0 || showCreateOption);
    return (_jsxs("div", { className: ['autocomplete-field', className].filter(Boolean).join(' '), children: [label && (_jsx("label", { className: "autocomplete-field__label", htmlFor: inputId, children: label })), _jsxs(Popover.Root, { open: open, onOpenChange: setOpen, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsxs("div", { ref: controlRef, className: [
                                'autocomplete-field__input-wrap',
                                prefix ? 'autocomplete-field__input-wrap--has-prefix' : '',
                            ]
                                .filter(Boolean)
                                .join(' '), children: [prefix && (_jsx("span", { ref: prefixRef, className: "autocomplete-field__prefix", "aria-hidden": "true", children: prefix })), _jsx("input", { ref: inputRef, id: inputId, name: name, type: "text", className: [
                                        'text-field__input',
                                        size === 'comfortable' ? 'text-field__input--comfortable' : '',
                                        error ? 'text-field__input--error' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' '), style: prefixWidth ? { paddingLeft: prefixWidth + 4 } : undefined, value: value, placeholder: loading ? 'Loading…' : placeholder, disabled: disabled || loading, autoComplete: "off", "aria-autocomplete": "list", "aria-expanded": open, "aria-label": label ? undefined : ariaLabel, role: "combobox", "aria-haspopup": "listbox", onChange: (e) => {
                                        onChange(e.target.value);
                                        setOpen(true);
                                        setHighlightedIndex(-1);
                                    }, onFocus: () => {
                                        if (suppressNextFocusOpenRef.current) {
                                            suppressNextFocusOpenRef.current = false;
                                            return;
                                        }
                                        if (!disabled && !loading)
                                            setOpen(true);
                                    }, onBlur: () => {
                                        if (normalize && value)
                                            onChange(normalize(value));
                                    }, onKeyDown: handleKeyDown }), !disabled && (_jsxs("span", { className: "autocomplete-field__ac-badge", "aria-hidden": "true", children: [_jsx("span", { className: "autocomplete-field__ac-badge-head", children: "aut" }), _jsx("span", { className: "autocomplete-field__ac-badge-tail", children: "o" })] }))] }) }), showDropdown && (_jsx(Popover.Portal, { children: _jsx(Popover.Content, { className: "autocomplete-field__content", align: "start", sideOffset: 4, onOpenAutoFocus: (e) => e.preventDefault(), onFocusOutside: (e) => {
                                // Prevent Radix from closing when focus stays within our input wrapper
                                if (controlRef.current?.contains(e.target)) {
                                    e.preventDefault();
                                }
                            }, onInteractOutside: (e) => {
                                // Prevent Radix from closing when pointer interacts within our input wrapper
                                if (controlRef.current?.contains(e.target)) {
                                    e.preventDefault();
                                }
                            }, children: _jsxs("ul", { className: "autocomplete-field__list", role: "listbox", "aria-label": label ?? 'Suggestions', children: [filteredOptions.map((option, index) => (_jsx("li", { className: [
                                            'autocomplete-field__item',
                                            index === highlightedIndex ? 'autocomplete-field__item--highlighted' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' '), role: "option", "aria-selected": index === highlightedIndex, onClick: () => handleSelect(option), tabIndex: -1, children: option }, option))), showCreateOption && (_jsxs("li", { className: [
                                            'autocomplete-field__item',
                                            'autocomplete-field__item--create',
                                            highlightedIndex === filteredOptions.length
                                                ? 'autocomplete-field__item--highlighted'
                                                : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' '), role: "option", "aria-selected": highlightedIndex === filteredOptions.length, onClick: () => handleSelect(value.trim()), tabIndex: -1, children: [_jsx("span", { className: "autocomplete-field__create-label", children: "Use" }), "\u201C", value.trim(), "\u201D"] }))] }) }) }))] }), error && (_jsx("p", { className: "text-field__error", role: "alert", children: error }))] }));
}
//# sourceMappingURL=AutocompleteField.js.map